import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { searchProducts } from "@/lib/api-client";
import { ProductCard } from "@/components/product/ProductCard";

/**
 * Équivalent de apps/web/app/(shop)/recherche/page.tsx — cible du formulaire
 * du header (`action="/recherche" method="get"`, champ `q`) et de l'onglet
 * "Recherche" de la barre d'onglets mobile.
 *
 * `q` est requis côté API (`SearchQueryDto.q` `@IsNotEmpty()`) : un `q`
 * vide/absent affiche un état neutre sans appeler l'API, plutôt que de
 * déclencher un 400 — `enabled: q.length > 0` reproduit ce garde-fou.
 *
 * Pagination « Voir plus » : comme l'original (un `Link` vers
 * `?cursor=...`, pas un état accumulé côté client), chaque clic remplace la
 * page affichée par celle du curseur suivant plutôt que d'empiler les
 * résultats déjà chargés — reproduit naturellement ici puisque `items` est
 * dérivé directement du résultat de la requête courante (URL = source de
 * vérité), sans état local intermédiaire.
 */
export function RecherchePage() {
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") ?? "").trim();
  const cursor = searchParams.get("cursor") ?? undefined;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["search", q, cursor],
    queryFn: () => searchProducts({ q, cursor }),
    enabled: q.length > 0,
  });

  if (!q) {
    return (
      <div className="container-vvs py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">Recherche</h1>
        <p className="mt-3 font-body text-sm text-muted">
          Saisissez un terme dans la barre de recherche ci-dessus pour trouver un produit ou une marque.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container-vvs py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">Résultats pour « {q} »</h1>
        <p className="mt-3 font-body text-sm text-muted">Recherche en cours…</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="container-vvs py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">Recherche</h1>
        <p className="mt-3 font-body text-sm text-muted">
          La recherche est momentanément indisponible. Réessayez dans un instant.
        </p>
      </div>
    );
  }

  const { items, nextCursor, suggestions } = data;
  const hasResults = items.length > 0;
  const hasFewResults = items.length < 5;

  return (
    <div className="container-vvs py-4">
      <header className="mb-4">
        <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">Résultats pour « {q} »</h1>
        {!cursor ? (
          <p className="mt-2 font-body text-sm text-muted">
            {hasResults
              ? `${items.length} produit${items.length > 1 ? "s" : ""} trouvé${items.length > 1 ? "s" : ""}${nextCursor ? " (au moins)" : ""}.`
              : "Aucun produit trouvé."}
          </p>
        ) : null}
      </header>

      {hasResults ? (
        <div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
            {items.map((item) => (
              <ProductCard key={item.id} product={item} />
            ))}
          </div>

          {nextCursor ? (
            <div className="mt-8 flex justify-center">
              <Link
                to={`/recherche?q=${encodeURIComponent(q)}&cursor=${encodeURIComponent(nextCursor)}`}
                className="rounded-sm border border-line bg-white px-6 py-2.5 font-body text-sm font-medium text-ink hover:border-navy"
              >
                Voir plus
              </Link>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="py-16 text-center">
          <p className="font-body text-base text-ink">Aucun produit ne correspond à « {q} ».</p>
          <p className="mt-2 font-body text-sm text-muted">
            Vérifiez l&apos;orthographe ou essayez un terme plus général.
          </p>
        </div>
      )}

      {suggestions.length > 0 && hasFewResults ? (
        <div className="mt-10 border-t border-line pt-6">
          <p className="font-body text-sm font-medium text-navy">Vous cherchiez peut-être :</p>
          {/* `suggestions[].slug` est un slug de PRODUIT, pas un terme de recherche à
              relancer : on lie donc directement vers la fiche produit. */}
          <ul className="mt-2 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/p/${s.slug}`}
                  className="inline-block rounded-full border border-line bg-white px-4 py-1.5 font-body text-sm text-ink hover:border-navy"
                >
                  {s.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
